<template>
    <label class="checkbox text-gray" :class="[`checkbox-${size}`, `checkbox-${layout}`, { 'checkbox-disabled': disabled }]">
        <input type="checkbox" :checked="modelValue" :disabled @change="updateValue(($event.target as HTMLInputElement).checked)">
        <span class="checkmark"></span>
        <span class="checkbox-label"><slot></slot></span>
    </label>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
    label?: string
    modelValue?: boolean
    disabled?: boolean
    size?: 'md' | 'sm'
    layout?: 'default' | 'inline'
}>(), {
    modelValue: false,
    disabled: false,
    size: 'md',
    layout: 'default'
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
}>();

const updateValue = (value: boolean) => {
    emit('update:modelValue', value);
};
</script>

<style scoped>
.checkbox {
    display: inline-flex;
    align-items: center;
    position: relative;
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    line-height: 20px;
}

.checkbox input {
    position: absolute;
    opacity: 0;
    cursor: inherit;
    height: 0;
    width: 0;
}

.checkbox:hover input:not(:disabled) + .checkmark {
    background-color: var(--checkbox-hover-bg);
}

.checkbox .checkmark {
    display: block;
    position: relative;
    box-sizing: border-box;
    flex: 0 0 auto;
    background-color: #f2f2f2;
    border-radius: var(--checkbox-radius);
}

.checkbox .checkmark::after {
    content: '';
    position: absolute;
    display: none;
    left: 7px;
    top: 3px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 1px 1px 0;
    transform: rotate(45deg);
}

.checkbox input:checked + .checkmark {
    background-color: var(--checkbox-checked-bg);
}

.checkbox input:checked + .checkmark::after {
    display: block;
}

.checkbox-label {
    line-height: inherit;
}

.checkbox-default .checkmark {
    position: absolute;
    top: 0;
    left: 0;
}

.checkbox-md {
    min-height: var(--checkbox-size);
    padding-left: 29px;
}

.checkbox-md.checkbox-default {
    display: block;
    margin-bottom: 12px;
}

.checkbox-md.checkbox-default .checkmark {
    top: 1px;
}

.checkbox-sm {
    min-height: 16px;
    padding-left: 24px;
    line-height: 16px;
}

.checkbox-inline {
    gap: 8px;
    padding-left: 0;
}

.checkbox-inline .checkmark {
    position: relative;
    top: auto;
    left: auto;
}

.checkbox-md .checkmark {
    height: var(--checkbox-size);
    width: var(--checkbox-size);
}

.checkbox-sm .checkmark {
    height: 16px;
    width: 16px;
}

.checkbox-sm .checkmark:after {
    left: 5px;
    top: 2px;
    width: 5px;
    height: 9px;
}

.checkbox-disabled {
    cursor: not-allowed;
    opacity: .5;
}
</style>
